/**
 * Event-driven state reconstructor for rendering.
 *
 * We don't re-run the engine — we walk the recorded events and mutate a
 * derived RenderState. This is fine for a spectator UI: everything a
 * viewer needs is already in the event stream (positions, inventories,
 * placed landmarks, drops, treasure holder, etc.).
 *
 * For fast scrubbing, we cache a full snapshot every SNAPSHOT_STRIDE events
 * and rebuild by starting from the nearest snapshot and stepping forward.
 */

import type { EventType } from "@bobt/engine";
import type {
  EnvelopePlayer,
  Hex,
  ReplayEnvelope,
  ReplayEvent,
} from "./types.js";

export interface RenderPlayer {
  id: string;
  color: string;
  character_id: string | null;
  inventory: string[];
  pirate: { position: Hex; in_boat: boolean };
  boat_position: Hex;
  known_treasure_landmark_id: string | null;
  stashes: { cell_key: string }[];
  eliminated: boolean;
}

export interface RenderState {
  turn: number;
  active_player_id: string | null;
  players: RenderPlayer[];
  active_crates: string[];
  placed_landmarks: { landmark_id: string; cell_key: string }[];
  placed_bridges: string[];
  dropped_items: { cell_key: string; items: string[] }[];
  hidden_treasure_landmark_id: string | null;
  treasure_dug: boolean;
  treasure_holder_id: string | null;
  /** Quack Hawkins mercantile cell key (Wave 2). null = not placed. */
  mercantile_cell_key: string | null;
  result_status: "in_progress" | "won" | "safety_cap";
  winner_id: string | null;
  event_log: string[];
  warnings: string[];
}

const SNAPSHOT_STRIDE = 40;

export const KNOWN_EVENT_TYPES = [
  "GAME_CREATED",
  "MAP_GENERATED",
  "HIDDEN_LANDMARK_SELECTED",
  "PLAYER_SETUP",
  "TURN_STARTED",
  "DIE_ROLLED",
  "MOVE_STARTED",
  "PIRATE_MOVED_STEP",
  "BOAT_MOVED_STEP",
  "BOAT_ENTERED",
  "BOAT_EXITED",
  "CRATE_OPENED",
  "TOOL_DRAWN",
  "CRATE_RELOCATED",
  "ITEM_PICKED_UP",
  "ITEM_DROPPED",
  "SWORD_USED",
  "ITEM_STOLEN",
  "PIRATE_RETURNED_TO_BOAT",
  "COMPASS_USED",
  "TREASURE_LANDMARK_LEARNED",
  "MAP_LANDMARK_PLACED",
  "MAP_TRAVEL_USED",
  "BRIDGE_PLACED",
  "STASH_BURIED",
  "STASH_RECOVERED",
  "ALBATROSS_USED",
  "TREASURE_DUG",
  "TREASURE_STOLEN",
  "TURN_ENDED",
  "GAME_WON",
  "AGENT_INVALID_DECISION",
  "CHARACTER_ASSIGNED",
  "TURN_SKIPPED",
  "PAXIE_PAN_MEAL_COOKED",
  "SCURVY_SAILOR_BONUS",
  "RED_BEARD_KEEP_CHOSEN",
  "DUTCHMAN_LAND_CROSSED",
  "BRIDGE_DISASSEMBLED",
  "MERCANTILE_PLACED",
  "MERCANTILE_PICKED_UP",
  "MERCANTILE_SWAPPED",
  "LANDMARKS_SWAPPED",
  "PRE_MOVE_COMMITTED",
  "BUCCANEER_SKIPPED_ACTION",
  "BONNY_DRAG_STARTED",
  "BONNY_DRAG_STEP",
  "BONNY_DRAG_ENDED",
  "BILLY_CANNON_FIRED",
  "STICKY_FINGERS_BORROWED",
  "MOCKINGBIRD_STOLE",
  "MOCKINGBIRD_RELEASED",
] as const satisfies readonly EventType[];

type KnownReplayEventType = (typeof KNOWN_EVENT_TYPES)[number];
type MissingEventType = Exclude<EventType, KnownReplayEventType>;
const EVENT_TYPE_COVERAGE: MissingEventType extends never ? true : never = true;
void EVENT_TYPE_COVERAGE;

const KNOWN_EVENT_TYPE_SET = new Set<string>(KNOWN_EVENT_TYPES);

export function isKnownEventType(type: string): type is KnownReplayEventType {
  return KNOWN_EVENT_TYPE_SET.has(type);
}

function initialState(env: ReplayEnvelope): RenderState {
  const players: RenderPlayer[] = env.initial_setup.players.map(
    (p: EnvelopePlayer) => ({
      id: p.id,
      color: p.color,
      character_id: p.character_id,
      inventory: [...p.starting_inventory],
      pirate: { position: { ...p.pirate_start }, in_boat: true },
      boat_position: { ...p.boat_start },
      known_treasure_landmark_id: null,
      stashes: [],
      eliminated: false,
    }),
  );
  return {
    turn: 0,
    active_player_id: null,
    players,
    active_crates: [...env.initial_setup.active_crate_cells],
    placed_landmarks: [],
    placed_bridges: [],
    dropped_items: [],
    hidden_treasure_landmark_id: null,
    treasure_dug: false,
    treasure_holder_id: null,
    mercantile_cell_key: null,
    result_status: "in_progress",
    winner_id: null,
    event_log: [],
    warnings: [],
  };
}

function cloneState(s: RenderState): RenderState {
  return {
    turn: s.turn,
    active_player_id: s.active_player_id,
    players: s.players.map((p) => ({
      id: p.id,
      color: p.color,
      character_id: p.character_id,
      inventory: [...p.inventory],
      pirate: { position: { ...p.pirate.position }, in_boat: p.pirate.in_boat },
      boat_position: { ...p.boat_position },
      known_treasure_landmark_id: p.known_treasure_landmark_id,
      stashes: p.stashes.map((x) => ({ cell_key: x.cell_key })),
      eliminated: p.eliminated,
    })),
    active_crates: [...s.active_crates],
    placed_landmarks: s.placed_landmarks.map((l) => ({ ...l })),
    placed_bridges: [...s.placed_bridges],
    dropped_items: s.dropped_items.map((d) => ({
      cell_key: d.cell_key,
      items: [...d.items],
    })),
    hidden_treasure_landmark_id: s.hidden_treasure_landmark_id,
    treasure_dug: s.treasure_dug,
    treasure_holder_id: s.treasure_holder_id,
    mercantile_cell_key: s.mercantile_cell_key,
    result_status: s.result_status,
    winner_id: s.winner_id,
    event_log: [...s.event_log],
    warnings: [...s.warnings],
  };
}

function findPlayer(s: RenderState, id: string | null): RenderPlayer | null {
  if (!id) return null;
  return s.players.find((p) => p.id === id) ?? null;
}

function humanize(e: ReplayEvent): string {
  const pid = e.player_id ? `${e.player_id.padEnd(6)}` : "      ";
  switch (e.type) {
    case "TURN_STARTED":
      return `T${e.turn} · ${pid} · turn starts`;
    case "DIE_ROLLED":
      return `T${e.turn} · ${pid} · rolled ${e.payload["roll"]}`;
    case "MOVE_STARTED":
      return `T${e.turn} · ${pid} · move budget ${e.payload["budget"]}`;
    case "PIRATE_MOVED_STEP":
    case "BOAT_MOVED_STEP":
      return `T${e.turn} · ${pid} · ${e.type === "BOAT_MOVED_STEP" ? "boat" : "step"}`;
    case "BOAT_ENTERED":
      return `T${e.turn} · ${pid} · boards boat`;
    case "BOAT_EXITED":
      return `T${e.turn} · ${pid} · disembarks`;
    case "CRATE_OPENED":
      return `T${e.turn} · ${pid} · opens a crate`;
    case "TOOL_DRAWN":
      return `T${e.turn} · ${pid} · drew ${e.payload["tool"] ?? "nothing"}`;
    case "CRATE_RELOCATED":
      return `T${e.turn} · ${pid} · relocates crate to ${e.payload["to"]}`;
    case "ITEM_PICKED_UP":
      return `T${e.turn} · ${pid} · picks up ${e.payload["item"]}`;
    case "ITEM_DROPPED":
      return `T${e.turn} · ${pid} · drops ${e.payload["item"]}`;
    case "SWORD_USED":
      return `T${e.turn} · ${pid} · attacks ${e.payload["target"]} (${e.payload["effect"]})`;
    case "ITEM_STOLEN":
      return `T${e.turn} · ${pid} · steals ${e.payload["item"]} from ${e.payload["from"]}`;
    case "TREASURE_STOLEN":
      return `T${e.turn} · ${pid} · STEALS TREASURE from ${e.payload["from"]}`;
    case "PIRATE_RETURNED_TO_BOAT":
      return `T${e.turn} · ${pid} · sent back to boat`;
    case "COMPASS_USED":
      return `T${e.turn} · ${pid} · reads compass`;
    case "MAP_LANDMARK_PLACED":
      return `T${e.turn} · ${pid} · places landmark ${e.payload["landmark_id"]} at ${e.payload["at"]}`;
    case "MAP_TRAVEL_USED":
      return `T${e.turn} · ${pid} · teleports to ${e.payload["landmark_id"]}`;
    case "BRIDGE_PLACED":
      return `T${e.turn} · ${pid} · builds a bridge at ${e.payload["at"]}`;
    case "STASH_BURIED":
      return `T${e.turn} · ${pid} · buries stash (${(e.payload["items"] as unknown[])?.length ?? 0} items)`;
    case "STASH_RECOVERED":
      return `T${e.turn} · ${pid} · recovers stash`;
    case "ALBATROSS_USED":
      return `T${e.turn} · ${pid} · rides albatross home`;
    case "TREASURE_DUG":
      return `T${e.turn} · ${pid} · DIGS UP THE TREASURE`;
    case "TURN_ENDED":
      return `T${e.turn} · ${pid} · turn ends`;
    case "GAME_WON":
      return `T${e.turn} · ${pid} · WINS THE GAME`;
    case "AGENT_INVALID_DECISION":
      return `T${e.turn} · ${pid} · invalid decision — fallback`;
    default:
      return `T${e.turn} · ${pid} · ${e.type}`;
  }
}

function applyEvent(state: RenderState, e: ReplayEvent): void {
  state.turn = e.turn;
  if (e.player_id) state.active_player_id = e.player_id;
  const player = findPlayer(state, e.player_id);
  switch (e.type) {
    case "PIRATE_MOVED_STEP": {
      const to = e.payload["to"] as Hex;
      if (player && to) {
        player.pirate.position = { ...to };
        player.pirate.in_boat = false;
      }
      break;
    }
    case "BOAT_MOVED_STEP": {
      const to = e.payload["to"] as Hex;
      if (player && to) {
        player.pirate.position = { ...to };
        player.boat_position = { ...to };
        player.pirate.in_boat = true;
      }
      break;
    }
    case "BOAT_ENTERED": {
      const at = e.payload["at"] as Hex;
      if (player && at) {
        player.pirate.position = { ...at };
        player.pirate.in_boat = true;
        player.boat_position = { ...at };
      }
      break;
    }
    case "BOAT_EXITED": {
      const to = e.payload["to"] as Hex;
      const from = e.payload["from"] as Hex;
      if (player) {
        if (to) player.pirate.position = { ...to };
        player.pirate.in_boat = false;
        if (from) player.boat_position = { ...from };
      }
      break;
    }
    case "CRATE_OPENED": {
      const at = e.payload["at"] as string | undefined;
      if (at) state.active_crates = state.active_crates.filter((k) => k !== at);
      break;
    }
    case "CRATE_RELOCATED": {
      const to = e.payload["to"] as string | undefined;
      if (to && !state.active_crates.includes(to)) state.active_crates.push(to);
      break;
    }
    case "TOOL_DRAWN": {
      const tool = e.payload["tool"];
      if (typeof tool === "string" && player) player.inventory.push(tool);
      break;
    }
    case "ITEM_PICKED_UP": {
      const item = e.payload["item"];
      const from = e.payload["from"];
      if (typeof item === "string" && player && typeof from === "string") {
        const stack = state.dropped_items.find((d) => d.cell_key === from);
        if (stack) {
          const idx = stack.items.indexOf(item);
          if (idx !== -1) stack.items.splice(idx, 1);
          if (stack.items.length === 0) {
            state.dropped_items = state.dropped_items.filter(
              (d) => d.cell_key !== from,
            );
          }
        }
        player.inventory.push(item);
      }
      break;
    }
    case "ITEM_DROPPED": {
      const item = e.payload["item"];
      const at = e.payload["at"];
      if (player && typeof item === "string") {
        const idx = player.inventory.indexOf(item);
        if (idx !== -1) player.inventory.splice(idx, 1);
      }
      const returnedToBag = e.payload["returned_to_bag"] === true;
      if (!returnedToBag && typeof at === "string" && typeof item === "string") {
        const stack = state.dropped_items.find((d) => d.cell_key === at);
        if (stack) stack.items.push(item);
        else state.dropped_items.push({ cell_key: at, items: [item] });
      }
      break;
    }
    case "ITEM_STOLEN": {
      const item = e.payload["item"];
      const from = e.payload["from"];
      const to = e.payload["to"];
      if (typeof item === "string" && typeof from === "string") {
        const victim = findPlayer(state, from);
        if (victim) {
          const idx = victim.inventory.indexOf(item);
          if (idx !== -1) victim.inventory.splice(idx, 1);
        }
      }
      if (typeof item === "string" && typeof to === "string") {
        const thief = findPlayer(state, to);
        if (thief) thief.inventory.push(item);
      }
      break;
    }
    case "TREASURE_STOLEN": {
      const from = e.payload["from"];
      const to = e.payload["to"];
      if (typeof from === "string") {
        const victim = findPlayer(state, from);
        if (victim) {
          const idx = victim.inventory.indexOf("treasure");
          if (idx !== -1) victim.inventory.splice(idx, 1);
        }
      }
      if (typeof to === "string") {
        const thief = findPlayer(state, to);
        if (thief) thief.inventory.push("treasure");
        state.treasure_holder_id = String(to);
      }
      break;
    }
    case "PIRATE_RETURNED_TO_BOAT": {
      if (player) {
        player.pirate.position = { ...player.boat_position };
        player.pirate.in_boat = true;
      }
      break;
    }
    case "COMPASS_USED":
      // Nothing observable in public state; TREASURE_LANDMARK_LEARNED (spectator)
      // sets the private knowledge.
      break;
    case "TREASURE_LANDMARK_LEARNED": {
      const landmark = e.payload["landmark_id"];
      if (player && typeof landmark === "string") {
        player.known_treasure_landmark_id = landmark;
      }
      break;
    }
    case "HIDDEN_LANDMARK_SELECTED": {
      const landmark = e.payload["landmark_id"];
      if (typeof landmark === "string") {
        state.hidden_treasure_landmark_id = landmark;
      }
      break;
    }
    case "MAP_LANDMARK_PLACED": {
      const landmark = e.payload["landmark_id"];
      const at = e.payload["at"];
      if (typeof landmark === "string" && typeof at === "string") {
        state.placed_landmarks.push({ landmark_id: landmark, cell_key: at });
      }
      break;
    }
    case "MAP_TRAVEL_USED": {
      const at = e.payload["to"];
      if (player && typeof at === "string") {
        const [q, r] = at.split(",").map(Number);
        if (Number.isFinite(q) && Number.isFinite(r)) {
          player.pirate.position = { q: q!, r: r! };
          player.pirate.in_boat = false;
        }
      }
      break;
    }
    case "BRIDGE_PLACED": {
      const at = e.payload["at"];
      if (typeof at === "string" && !state.placed_bridges.includes(at)) {
        state.placed_bridges.push(at);
      }
      if (player) {
        const idx = player.inventory.indexOf("bridge");
        if (idx !== -1) player.inventory.splice(idx, 1);
      }
      break;
    }
    case "STASH_BURIED": {
      const at = e.payload["at"];
      const items = e.payload["items"] as string[] | undefined;
      if (player && typeof at === "string") {
        player.stashes.push({ cell_key: at });
        if (items) {
          for (const item of items) {
            const idx = player.inventory.indexOf(item);
            if (idx !== -1) player.inventory.splice(idx, 1);
          }
        }
      }
      break;
    }
    case "STASH_RECOVERED": {
      const items = e.payload["items"] as string[] | undefined;
      if (player && items) {
        for (const item of items) player.inventory.push(item);
      }
      break;
    }
    case "ALBATROSS_USED": {
      if (player) {
        player.pirate.position = { ...player.boat_position };
        player.pirate.in_boat = true;
      }
      break;
    }
    case "TREASURE_DUG": {
      state.treasure_dug = true;
      if (player) {
        player.inventory.push("treasure");
        state.treasure_holder_id = player.id;
        const at = e.payload["at"];
        if (typeof at === "string") {
          const landmark = state.placed_landmarks.find((l) => l.cell_key === at);
          if (landmark) {
            player.known_treasure_landmark_id = landmark.landmark_id;
          }
        }
      }
      break;
    }
    case "GAME_WON": {
      state.result_status = "won";
      const winner = e.payload["winner"];
      if (typeof winner === "string") state.winner_id = winner;
      break;
    }
    // --- Wave 2 events ---
    case "DUTCHMAN_LAND_CROSSED":
      // Log-only; no state change beyond the position already updated by BOAT_MOVED_STEP.
      break;
    case "BRIDGE_DISASSEMBLED": {
      const at = e.payload["at"];
      if (typeof at === "string") {
        state.placed_bridges = state.placed_bridges.filter((k) => k !== at);
      }
      if (player) player.inventory.push("bridge");
      break;
    }
    case "MERCANTILE_PLACED": {
      const at = e.payload["at"];
      if (typeof at === "string") state.mercantile_cell_key = at;
      break;
    }
    case "MERCANTILE_PICKED_UP":
      state.mercantile_cell_key = null;
      break;
    case "MERCANTILE_SWAPPED": {
      const gave = e.payload["gave"];
      const took = e.payload["took"];
      if (player && typeof gave === "string") {
        const idx = player.inventory.indexOf(gave);
        if (idx !== -1) player.inventory.splice(idx, 1);
      }
      if (player && typeof took === "string") player.inventory.push(took);
      break;
    }
    case "LANDMARKS_SWAPPED": {
      const swapped = e.payload["swapped"];
      if (Array.isArray(swapped)) {
        for (const pair of swapped as { landmark_id: string; new_at: string }[]) {
          const target = state.placed_landmarks.find(
            (l) => l.landmark_id === pair.landmark_id,
          );
          if (target) target.cell_key = pair.new_at;
        }
      }
      break;
    }
    // Wave 1 events already handled above; the remaining ones are log-only
    // (no derived-state impact) and fall through here.
    case "CHARACTER_ASSIGNED":
    case "TURN_SKIPPED":
    case "PAXIE_PAN_MEAL_COOKED":
    case "SCURVY_SAILOR_BONUS":
    case "RED_BEARD_KEEP_CHOSEN":
      break;
    // Wave 2b — Bonny's dragged target's position update is emitted as a
    // sibling event that carries the target + destination. Apply it so the
    // UI shows the dragged pirate moving.
    case "BONNY_DRAG_STEP": {
      const targetId = e.payload["target_player_id"];
      const to = e.payload["to"] as { q: number; r: number } | undefined;
      if (typeof targetId === "string" && to) {
        const target = findPlayer(state, targetId);
        if (target) {
          target.pirate.position = { ...to };
          target.pirate.in_boat = false;
        }
      }
      break;
    }
    case "PRE_MOVE_COMMITTED":
    case "BUCCANEER_SKIPPED_ACTION":
    case "BONNY_DRAG_STARTED":
    case "BONNY_DRAG_ENDED":
      break;
    // Wave 3 — Billy's cannon relocates target pirate + boat.
    case "BILLY_CANNON_FIRED": {
      const targetId = e.payload["target"];
      const bpos = e.payload["target_boat_new"] as { q: number; r: number } | undefined;
      const ppos = e.payload["target_pirate_new"] as { q: number; r: number } | undefined;
      if (typeof targetId === "string") {
        const t = findPlayer(state, targetId);
        if (t) {
          if (bpos) t.boat_position = { ...bpos };
          if (ppos) {
            t.pirate.position = { ...ppos };
            t.pirate.in_boat = false;
          }
        }
      }
      break;
    }
    case "STICKY_FINGERS_BORROWED":
      // Log-only marker; the accompanying MAP_TRAVEL_USED / BRIDGE_PLACED /
      // TREASURE_LANDMARK_LEARNED / etc events actually mutate state.
      break;
    case "MOCKINGBIRD_STOLE":
    case "MOCKINGBIRD_RELEASED":
      // No player-position or inventory change; log-only for now.
      break;
    default:
      state.warnings.push(`Unknown event type ${e.type} at seq ${e.seq}`);
      break;
  }
  state.event_log.push(humanize(e));
  if (state.event_log.length > 500) {
    state.event_log = state.event_log.slice(-500);
  }
}

export class ReplayEngine {
  private envelope: ReplayEnvelope;
  private events: ReplayEvent[];
  private snapshots: Map<number, RenderState> = new Map();

  constructor(envelope: ReplayEnvelope, events: ReplayEvent[]) {
    this.envelope = envelope;
    this.events = events;
    this.snapshots.set(0, initialState(envelope));
  }

  stateAt(index: number): RenderState {
    // Clamp.
    const target = Math.max(0, Math.min(index, this.events.length));
    // Find nearest snapshot ≤ target.
    let nearest = 0;
    for (const key of this.snapshots.keys()) {
      if (key <= target && key > nearest) nearest = key;
    }
    const s = cloneState(this.snapshots.get(nearest)!);
    for (let i = nearest; i < target; i++) {
      applyEvent(s, this.events[i]!);
      if ((i + 1) % SNAPSHOT_STRIDE === 0 && !this.snapshots.has(i + 1)) {
        this.snapshots.set(i + 1, cloneState(s));
      }
    }
    if (target === this.events.length) {
      s.result_status = this.envelope.result.status;
      s.winner_id = this.envelope.result.winnerId;
    }
    return s;
  }

  eventCount(): number {
    return this.events.length;
  }

  eventAt(index: number): ReplayEvent | undefined {
    return this.events[index];
  }

  /** Find first event of a given type at or after `fromIndex`. */
  findEventIndex(
    predicate: (e: ReplayEvent) => boolean,
    fromIndex = 0,
  ): number {
    for (let i = fromIndex; i < this.events.length; i++) {
      if (predicate(this.events[i]!)) return i;
    }
    return -1;
  }

  /** Group event indices by turn (used to step turn-by-turn). */
  turnBoundaries(): number[] {
    const bounds: number[] = [];
    let lastTurn = -1;
    for (let i = 0; i < this.events.length; i++) {
      const t = this.events[i]!.turn;
      if (t !== lastTurn) {
        bounds.push(i);
        lastTurn = t;
      }
    }
    return bounds;
  }
}
